import { type FC, useCallback, useState } from 'react';

import { ProfileUI } from '@crehana/compass.layouts';
import { useTranslation } from '@crehana/i18n';
import NewArrowLeft from '@crehana/icons/NewArrowLeft';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { Col, Container, PrimaryButton, Row, TextLink } from '@crehana/ui';
import { useUITheme } from '@crehana/web';
import { clsx } from 'clsx';
import { useRouter } from 'next/router';

import useAdminDashboard from '../../hooks/useAdminDashboard';
import useFlagOrganization from '../../hooks/useFlagOrganization';
import { adminLocaleEs } from '../../i18n';
import Search from './components/Search';
import type { HeaderProps, MainMenuItemsType } from './types';

export const mainMenuItems = {
  Search,
  ProfileUI,
};

const Header: FC<React.PropsWithChildren<HeaderProps>> = ({
  title,
  subtitle,
  isDarkBaseMain,
  backBtn,
  cta,
  customTitle,
  transparent,
  children,
}) => {
  const { asPath } = useRouter();
  const { t } = useTranslation();
  const { isDark } = useUITheme();
  const { slug } = useAdminDashboard();
  const [itemOpen, setItemOpen] = useState<MainMenuItemsType | null>(null);
  const { isActive: isTalent, isLoading } = useFlagOrganization({
    flag: 'talent-suite-cross-navigation',
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  const toggleItem = useCallback(
    (item: MainMenuItemsType | null) =>
      item === itemOpen ? setItemOpen(null) : setItemOpen(item),
    [setItemOpen, itemOpen],
  );

  return (
    <div className={'tw-w-full'}>
      <div
        className={clsx('tw-w-full', {
          'tw-bg-neutral-light-100': !transparent && !isDark,
          'tw-bg-elevation-dark-02': isDark && isDarkBaseMain,
        })}
      >
        <Container
          type="dashboard"
          className={title ? 'py-32' : 'tw-pt-1.6 tw-pb-0.8'}
        >
          <Row>
            <Col
              xs={12}
              sm={12}
              md={12}
              lg={12}
              className={!title ? 'sm:pb-0' : ''}
            >
              {!isTalent && !isLoading && (
                <div
                  className={clsx(
                    'flex items-center mb-16',
                    backBtn ? 'justify-between' : 'justify-end',
                  )}
                >
                  {backBtn && (
                    <TextLink
                      type="level2"
                      leftIcon={{
                        component: NewArrowLeft,
                        size: 24,
                      }}
                      label={t('HEADER_BACK_BUTTON_VIEW', {
                        defaultValue: adminLocaleEs.es.HEADER_BACK_BUTTON_VIEW,
                      })}
                      {...backBtn}
                    />
                  )}
                  <div className="flex items-center">
                    {Object.entries(mainMenuItems)
                      .map(item => ({
                        key: item[0] as MainMenuItemsType,
                        component: item[1],
                      }))
                      .map(item => (
                        <item.component
                          key={item.key}
                          slug={item.key}
                          isDark={isDark || isDarkBaseMain}
                          open={itemOpen === item.key}
                          toggleItem={toggleItem}
                          setItemOpen={setItemOpen}
                          currentUrl={asPath}
                          organizationSlug={slug}
                        />
                      ))}
                  </div>
                </div>
              )}
              {children}
              {customTitle
                ? customTitle
                : title && (
                    <div className="flex items-end justify-between">
                      <div>
                        <h3 className="tw-font-h4 tw-font-semibold mb-8">
                          {title}
                        </h3>
                        <p
                          className={`tw-font-body4 tw-max-w-7xl ${
                            !isDark
                              ? 'tw-text-neutral-light-700'
                              : 'tw-text-neutral-dark-400'
                          }`}
                        >
                          {subtitle}
                        </p>
                      </div>
                      {cta && (
                        <PrimaryButton
                          {...cta}
                          darkMode={isDark}
                          size="s"
                          data-tour="header-page-cta"
                        />
                      )}
                    </div>
                  )}
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
};

export default Header;
